import { defineComponent, nextTick, h } from 'vue';
import { QTree, QIcon } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';

export default defineComponent({
    name: 'dynamic-tree',
    mixins: [BaseComponent],
    components: { QTree, QIcon },
    data: function () {
        return {
            unique_id: null,
            selected_values: [],
            selected_key: null,
            expanded_keys: [],

            model: [],
            allkeys: {},

            sourceraw: null,
            sourceurl: null,
            icon_fn: null,
            iconTooltip_fn: null,
            iconColor_fn: null,
            autoExpandAll_fn: null,
            lazyLoadChildren_fn: null,
            dropdata_fn: null,
            lazy_loaded_keys: {},
            lazy_loading_keys: {},

            watch_source$: null,
        }
    },
    created() {
        this.unique_id = `tree_${utils.generateUUID()}`;

        if (this.controlData.SourceType === 'Raw' && this.controlData.SourceRaw) {
            this.watch_source$ = this.$watch('sourcerawvalue', function () { this.Refresh(); });
        }
        else if (this.controlData.SourceType === 'Url' && this.controlData.SourceUrl) {
            this.watch_source$ = this.$watch('sourceurlvalue', function () { this.Refresh(); });
        }

        if (this.controlData.IconExpression)
            this.icon_fn = utils.compileExpression(this, this.controlData.IconExpression, 'node');
        if (this.controlData.IconTooltipExpression)
            this.iconTooltip_fn = utils.compileExpression(this, this.Translate(this.controlData.IconTooltipExpression), 'node');
        if (this.controlData.IconColorExpression)
            this.iconColor_fn = utils.compileExpression(this, this.controlData.IconColorExpression, 'node');

        if (this.controlData.EnableDrag && this.controlData.DragData)
            this.dropdata_fn = utils.compileObject(this, this.controlData.DragData, 'item');
    },
    unmounted() {
        if (this.watch_source$)
            this.watch_source$();
    },
    //Created Replaced with preRenderComplete
    computed: {
        sourcerawvalue: function () {
            if (!this.sourceraw)
                this.sourceraw = utils.compileObject(this, this.controlData.SourceRaw);

            return utils.evaluateObject(this.sourceraw, this);
        },
        sourceurlvalue: function () {
            if (!this.sourceurl)
                this.sourceurl = utils.compile(this, this.controlData.SourceUrl);

            return utils.evaluate(this.sourceurl, this);
        },
        autoExpandAll: function () {
            if (typeof this.controlData.AutoExpandAll === 'string' && !this.autoExpandAll_fn)
                this.autoExpandAll_fn = utils.compileObject(this, this.controlData.AutoExpandAll);

            if (this.autoExpandAll_fn)
                return utils.evaluateObject(this.autoExpandAll_fn, this);

            return this.controlData.AutoExpandAll;
        },
        lazyLoadChildren: function () {
            if (typeof this.controlData.LazyLoadChildren === 'string' && !this.lazyLoadChildren_fn)
                this.lazyLoadChildren_fn = utils.compileObject(this, this.controlData.LazyLoadChildren);

            if (this.lazyLoadChildren_fn)
                return utils.evaluateObject(this.lazyLoadChildren_fn, this);

            return this.controlData.LazyLoadChildren;
        },
        treeNodes() {
            const model = this.model || [];
            const childKey = this.controlData.ItemChildren || 'children';
            if (childKey === 'children')
                return model;
            return this.mapTreeNodes(model, childKey);
        },
    },
    methods: {
        async preRenderComplete() {
            this.finishRenderHandler(this);

            this.Refresh();
        },
        saveAllKeys(items) {
            const itemkey = this.controlData.ItemKey || 'id';
            const itemchildren = this.controlData.ItemChildren || 'children';

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                this.allkeys[item[itemkey]] = item;

                if (item[itemchildren] && Array.isArray(item[itemchildren]))
                    this.saveAllKeys(item[itemchildren]);
            }
        },
        mapTreeNodes(items, childKey) {
            if (!items || !Array.isArray(items)) return [];
            const itemKey = this.controlData.ItemKey || 'id';
            const itemDisabled = this.controlData.ItemDisabled;
            return items.map(n => {
                const hasChildCollection = Array.isArray(n[childKey]);
                const children = hasChildCollection ? this.mapTreeNodes(n[childKey], childKey) : undefined;
                const isLazyNode = n.lazy === true || (this.lazyLoadChildren && hasChildCollection && children.length === 0);
                const hasChildren = !!(children && children.length) || isLazyNode;
                return {
                    ...n,
                    children,
                    lazy: isLazyNode,
                    selectable: !hasChildren,
                    ...(itemDisabled && (n[itemDisabled] === true || n[itemDisabled] === 'true') ? { disabled: true } : {}),
                };
            });
        },
        getAllExpandableKeys(items, keyProp, childKey) {
            if (!items || !Array.isArray(items)) return [];
            let keys = [];
            for (const n of items) {
                const children = n[childKey];
                if (children && Array.isArray(children) && children.length > 0) {
                    keys.push(n[keyProp]);
                    keys = keys.concat(this.getAllExpandableKeys(children, keyProp, childKey));
                }
            }
            return keys;
        },
        toggleExpandNode(key) {
            const idx = this.expanded_keys.indexOf(key);
            this.expanded_keys = idx >= 0
                ? this.expanded_keys.filter(k => k !== key)
                : [...this.expanded_keys, key];
        },
        resolveNodeSourceItem(nodeOrKey) {
            const itemKey = this.controlData.ItemKey || 'id';
            const key = typeof nodeOrKey === 'object' ? nodeOrKey?.[itemKey] : nodeOrKey;
            if (key != null && this.allkeys[key])
                return this.allkeys[key];
            return typeof nodeOrKey === 'object' ? nodeOrKey : null;
        },
        isExpandableNode(nodeOrKey) {
            const sourceItem = this.resolveNodeSourceItem(nodeOrKey);
            if (!sourceItem) return false;

            const childKey = this.controlData.ItemChildren || 'children';
            const children = sourceItem[childKey];
            const hasChildCollection = Array.isArray(children);

            if (hasChildCollection && children.length > 0)
                return true;

            return !!(sourceItem.lazy === true || (this.lazyLoadChildren && hasChildCollection));
        },
        /** Matches Quasar slot node for row expand; source-only isExpandableNode can miss lazy/empty shapes. */
        isHeaderExpandableNode(node) {
            if (!node) return false;
            if (this.isExpandableNode(node)) return true;
            const childKey = this.controlData.ItemChildren || 'children';
            if (node.lazy === true || node.lazy === 'loading') return true;
            const kids = node[childKey];
            if (this.lazyLoadChildren && Array.isArray(kids) && kids.length === 0) return true;
            if (Array.isArray(node.children) && node.children.length > 0) return true;
            if (this.lazyLoadChildren && Array.isArray(node.children) && node.children.length === 0) return true;
            return false;
        },
        async ensureNodeChildrenLoaded(nodeOrKey) {
            if (!this.lazyLoadChildren) return;

            const sourceItem = this.resolveNodeSourceItem(nodeOrKey);
            if (!sourceItem) return;

            const itemKey = this.controlData.ItemKey || 'id';
            const childKey = this.controlData.ItemChildren || 'children';
            const nodeKey = sourceItem[itemKey];

            if (!Array.isArray(sourceItem[childKey]) || this.lazy_loaded_keys[nodeKey] || this.lazy_loading_keys[nodeKey])
                return this.lazy_loading_keys[nodeKey];

            const loadingPromise = (async () => {
                await this.loadChildren(sourceItem);
                this.saveAllKeys(sourceItem[childKey] || []);
                this.lazy_loaded_keys[nodeKey] = true;
                this.model = [...(this.model || [])];
            })();

            this.lazy_loading_keys[nodeKey] = loadingPromise;
            try {
                await loadingPromise;
            }
            finally {
                delete this.lazy_loading_keys[nodeKey];
            }
        },
        async handleLazyLoad({ node, key, done, fail }) {
            const itemKey = this.controlData.ItemKey || 'id';
            const childKey = this.controlData.ItemChildren || 'children';
            const sourceItem = this.resolveNodeSourceItem(node || key);
            if (!sourceItem) {
                done([]);
                return;
            }

            try {
                await this.ensureNodeChildrenLoaded(sourceItem[itemKey]);
                done(sourceItem[childKey] || []);
            }
            catch (e) {
                utils.warn(`DynamicTree lazy load failed for key ${sourceItem[itemKey]}: ${e}`);
                fail();
            }
        },
        onExpandedUpdate(keys) {
            this.expanded_keys = keys;
        },

        async Refresh() {
            for (var prop in this.allkeys) {
                if (this.allkeys.hasOwnProperty(prop))
                    delete this.allkeys[prop];
            }
            this.lazy_loaded_keys = {};
            this.lazy_loading_keys = {};

            let model;
            if (this.sourceraw)
                try {
                    model = this.sourcerawvalue;
                }
                catch (e) {
                    utils.warn('DynamicTree failed to evaluate SourceRaw ' + this.controlData.SourceRaw + ': ' + e);
                }
            else if (this.sourceurl)
                try {
                    model = await utils.api.get(this.sourceurlvalue);
                }
                catch (e) {
                    utils.warn('DynamicTree failed to evaluate SourceUrl ' + this.controlData.SourceUrl + ': ' + e);
                }

            this.saveAllKeys(model || []);

            this.model = model;

            if (this.autoExpandAll && model && model.length) {
                const keyProp = this.controlData.ItemKey || 'id';
                const childKey = this.controlData.ItemChildren || 'children';
                this.expanded_keys = this.getAllExpandableKeys(model, keyProp, childKey);
            }
        },
        Redraw() {
            this.unique_id = `tree_${utils.generateUUID()}`;
        },

        SetChildItems(model) {
            // Add keys for newly added items
            for (let i = 0; i < model.length; i++)
                this.allkeys[model[i][this.controlData.ItemKey || 'id']] = model[i];

            this.added_children = model;
        },
        ExpandAll() {
            const c = this;
            if (this.unique_id in this.$refs)
                nextTick(function () {
                    const tree = c.$refs[c.unique_id];
                    if (tree && tree.expandAll) tree.expandAll();
                });
        },
        CollapseAll() {
            const c = this;
            if (this.unique_id in this.$refs)
                nextTick(function () {
                    const tree = c.$refs[c.unique_id];
                    if (tree && tree.collapseAll) tree.collapseAll();
                });
        },

        async loadChildren(item) {
            this.added_children = null;
            await utils.executeAndCompileAllActions(this.controlData.OnLazyLoadActions, { Data: item, Tree: this }, this);
            const itemchildren = this.controlData.ItemChildren || 'children';
            item[itemchildren] = item[itemchildren] || [];
            if (this.added_children) {
                item[itemchildren].push(...this.added_children);
            }
            item.children = item[itemchildren];
            this.added_children = null;
        },
        async onInput(selectedvalues) {
            // selectedvalues is an array of Ids that match the id property within the selected menu item
            if (selectedvalues.length > 0) {
                this.selected_values = selectedvalues;
                this.selected_key = selectedvalues[0];
                const value = this.allkeys[this.selected_key];

                //utils.log(`DynamicTree onInput:${JSON.stringify(selectedvalues)}; value:${JSON.stringify(value)}`);

                utils.executeAndCompileAllActions(this.controlData.SelectMenuActions, { Data: this.selected_key, SelectedItem: value }, this);
            }
        },
        onSelected(items) {
            this.selected_values = items;
            this.selected_key = items[0];

            const input = {
                Data: items[0],
                SelectedItem: this.allkeys[items[0]],
            };

            utils.executeAndCompileAllActions(this.controlData.SelectMenuActions, input, this);

        },

        handleDragStart(ev, item) {
            const data = this.dropdata_fn ? utils.evaluateObject(this.dropdata_fn, this, item) : {};
            ev.dataTransfer.effectAllowed = 'copy';
            ev.dataTransfer.setData('application/json', JSON.stringify(data));
            ev.dataTransfer.setDragImage(ev.target, 10, 10);
        },
        handleClick(ev, item) {

        },
        handleDoubleClick(ev, item) {

        },
    },
    render() {
        if(!this.todisplay)
            return null;
        
        try {

            const style = {
                minWidth: "25px",
                ...this.sizeStyle,
                ...utils.resolveStyleHints(this.styleHints, this),
                overflow: "auto",
            };

            const scopedSlots = {};

            if (this.controlData.PrependItemControls?.length) {
                scopedSlots.prepend = (node) => {
                    return <div class="d-flex mr-2">
                        {utils.generateItemsFromArray(h, this, this.controlData.PrependItemControls, node, 'HorizontalLayout', { onFinishedRender: (item) => this.finishRenderHandler(item) })}
                    </div>;

                } 
            }

            if (this.controlData.AppendItemControls?.length) {
                scopedSlots.append = (node) => {
                    return <div class="d-flex mr-2">
                        {utils.generateItemsFromArray(h, this, this.controlData.AppendItemControls, node, 'HorizontalLayout', { onFinishedRender: (item) => this.finishRenderHandler(item) })}
                    </div>;

                }
            }
            else if (this.controlData.IconExpression) {
                scopedSlots.append = (node) => {
                    let iconVal = utils.evaluate(this.icon_fn, this, false, null, false, node);
                    if (!iconVal) return undefined;
                    let color;
                    let tooltip;
                    if (this.iconColor_fn) color = utils.evaluate(this.iconColor_fn, this, false, null, false, node);
                    if (this.iconTooltip_fn) tooltip = utils.evaluate(this.iconTooltip_fn, this, false, null, false, node);
                    const iconName = typeof iconVal === 'string' ? iconVal.replace(/^\s*mdi\s+/, 'mdi-').trim() : iconVal;
                    const sizeMap = { 'Small': 'sm', 'Extra Small': 'xs' };
                    const iconSize = sizeMap[this.controlData.IconSize] || undefined;
                    let icon = <QIcon name={iconName} size={iconSize} style={{ color: color }} />;
                    if (tooltip) icon = utils.generateTooltip(h, icon, tooltip, 'right');
                    return icon;
                };
            }

            if (this.controlData.EnableDrag) {
                scopedSlots.title = ({ item, internalItem }) => {
                    const hasChildren = !!internalItem.children;
                    const label = internalItem.title;
                    if (!hasChildren)
                        return <div class="pt-2 tree-node" draggable={true} onDragstart={(e) => this.handleDragStart(e, item)} onDblclick={(e) => this.handleDoubleClick(e, item)}>{label}</div>;
                    return <div class="pt-2">{label}</div>;
                };
            }

            const itemKey = this.controlData.ItemKey || 'id';
            const itemText = this.controlData.ItemText || 'label';
            const self = this;
            const renderDefaultHeader = (scope) => {
                const node = scope.node;
                const item = node;
                const label = node[itemText];
                const hasChildren = self.isHeaderExpandableNode(node);
                const nodeKey = node[itemKey];
                const isSelectedLeaf = !hasChildren && self.selected_values && self.selected_values[0] === nodeKey;
                let titleContent = label;
                if (scopedSlots.title)
                    titleContent = scopedSlots.title({ item: node, internalItem: { title: label, children: node.children } });
                const prependContent = scopedSlots.prepend ? scopedSlots.prepend({ item: node }) : null;
                let appendContent = null;
                if (scopedSlots.append) {
                    const leaf = !hasChildren;
                    const selected = !!(self.selected_values && self.selected_values[0] === nodeKey);
                    const open = self.expanded_keys.indexOf(nodeKey) >= 0;
                    const active = selected;
                    const appendPayload = { item: node, leaf, selected, open, active };
                    appendContent = scopedSlots.append(appendPayload);
                }
                const onHeaderClick = (e) => {
                    if (hasChildren) {
                        e.stopPropagation();
                        e.preventDefault();
                        const tree = self.unique_id in self.$refs ? self.$refs[self.unique_id] : null;
                        const nextOpen = self.expanded_keys.indexOf(nodeKey) < 0;
                        if (tree && typeof tree.setExpanded === 'function') {
                            tree.setExpanded(nodeKey, nextOpen);
                        } else {
                            self.toggleExpandNode(nodeKey);
                        }
                    } else {
                        e.stopPropagation();
                        e.preventDefault();
                        self.onInput([nodeKey]);
                    }
                };
                const headerStyle = {
                    width: '100%',
                    minWidth: 0,
                    minHeight: '32px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                };
                const nodeRowStyle = {
                    ...headerStyle,
                    ...(isSelectedLeaf ? { backgroundColor: 'rgba(0, 102, 204, 0.12)', color: '#0066cc', borderRadius: '8px' } : {}),
                };
                return (
                    <div
                        class={['q-tree__node-header', isSelectedLeaf && 'c-DynamicTree-node--selected-leaf']}
                        style={nodeRowStyle}
                        onClick={onHeaderClick}
                    >
                        {prependContent}
                        <span class="q-ml-sm text-body1" style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>{titleContent}</span>
                        {appendContent && <span class="q-ml-sm" style={{ display: 'flex', alignItems: 'center' }}>{appendContent}</span>}
                    </div>
                );
            };

            const tv = (
                <QTree
                    ref={this.unique_id}
                    nodes={this.treeNodes}
                    node-key={itemKey}
                    label-key={itemText}
                    noConnectors
                    dense
                    class="ma-1 c-DynamicTree-tree"
                    style={{ backgroundColor: '#f2f2f2' }}
                    expanded={this.expanded_keys}
                    onUpdate:expanded={(keys) => this.onExpandedUpdate(keys)}
                    default-expand-all={this.autoExpandAll}
                    selected={this.selected_values && this.selected_values[0]}
                    onUpdate:selected={(key) => this.onInput(key != null ? [key] : [])}
                    onLazyLoad={this.lazyLoadChildren ? this.handleLazyLoad : undefined}
                    v-slots={{ 'default-header': renderDefaultHeader }}
                />
            );

            return (
                <div
                    class={{ 'c-DynamicTree': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                    style={style}>
                    {tv}
                </div>
            );
        }
        catch (e) {
            utils.error('DynamicTree Render failed', e);
            return <div>DynamicTree Failed to Render {e}</div>;
        }
    }
});